import { useEffect, useRef, useState } from 'react';
import { queryHelpers, useMediaQuery } from '@leagueplatform/genesis-core';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';

// The SDK renders the nav entries itself from a flat `{ to, label, icon }` shape
// with no slot for extra content, so anything attached to an entry has to be
// portalled into the rendered anchor. Both entries are matched on their route
// rather than their id: the SDK derives entry ids from the label, which is
// localized, while the route is not. Nothing else in the header links to the
// support route, so scoping to the nav is enough to make this unambiguous.
const ENTRY_SELECTOR = `a[href="${APP_MANAGED_ROUTES.SUPPORT}"]`;

// The collapsed menu is a Genesis Modal — a Radix dialog portalled to the end of
// `body`, outside this component's subtree — so it has to be matched from the
// document. The SDK gives that dialog a fixed id, which is what distinguishes it
// from any other modal a page might have open; matching the dialog role alone
// would let a page's own modal attract the indicator.
const COLLAPSED_MENU_SELECTOR = '#user_dropdown_menu';

type SupportNavEntries = {
  /** Attach to the header's own nav bar. */
  navRef: React.RefObject<HTMLDivElement>;
  /** The support entry in the horizontal nav, or null when it is collapsed. */
  horizontalEntry: HTMLElement | null;
  /** The support entry in the collapsed menu, or null while it is closed. */
  collapsedEntry: HTMLElement | null;
};

/**
 * Resolves the rendered support nav entries so content can be portalled into
 * them.
 *
 * @param supportLabel the current label of the support entry. The SDK keys each
 * rendered entry by its label, so an entry is a new element exactly when its
 * label changes — which happens after first paint, once the locale bundle
 * resolves. Re-resolving on it is what stops a captured element going stale and
 * silently orphaning whatever was portalled in.
 */
export const useSupportNavEntries = (
  supportLabel: string | undefined,
): SupportNavEntries => {
  const navRef = useRef<HTMLDivElement>(null);
  const [horizontalEntry, setHorizontalEntry] = useState<HTMLElement | null>(
    null,
  );
  const [collapsedEntry, setCollapsedEntry] = useState<HTMLElement | null>(
    null,
  );

  // Below the tablet breakpoint the SDK swaps the horizontal entries for a menu
  // trigger, so only one of the two entries exists at a time.
  const isCollapsedNav = useMediaQuery(queryHelpers.down('tablet'));

  useEffect(() => {
    setHorizontalEntry(
      navRef.current?.querySelector<HTMLElement>(ENTRY_SELECTOR) ?? null,
    );
  }, [supportLabel, isCollapsedNav]);

  useEffect(() => {
    // Only the collapsed nav has a menu to look in. Without this guard the
    // lookup would match any dialog carrying a link to the support route — a
    // modal opened over a page, say — and attach a second indicator to it at
    // full width, where the horizontal entry already has one.
    if (!isCollapsedNav) {
      setCollapsedEntry(null);
      return undefined;
    }

    const resolveCollapsedEntry = () =>
      setCollapsedEntry(
        document
          .querySelector(COLLAPSED_MENU_SELECTOR)
          ?.querySelector<HTMLElement>(ENTRY_SELECTOR) ?? null,
      );

    // Opening the menu is the SDK's own state, with no callback to subscribe to,
    // and it does not re-render this component. What it does do is mount the
    // dialog's portal container as a direct child of `body`, so watching only
    // `body`'s own child list catches every open and close without the cost of
    // observing the whole document.
    const observer = new MutationObserver(resolveCollapsedEntry);
    observer.observe(document.body, { childList: true });

    // The menu can already be open when this runs — on a label change, say.
    resolveCollapsedEntry();

    return () => observer.disconnect();
  }, [supportLabel, isCollapsedNav]);

  return { navRef, horizontalEntry, collapsedEntry };
};
